"use client";

import { LoginContext } from "@/context/useLoginProps";
import { useMobile } from "@/hooks";
import { EventTrackingProps, ToastType } from "@/types/common";
import trackClevertapEvents from "@/utils/trackClevertapEvents";
import { Toast } from "@monsterindia/global-ui-components";
import React, { useCallback, useEffect, useMemo, useState } from "react";

import LoginHeader from "@/components/common/LoginHeader";
import LoginLeft from "@/components/login/loginLeft";
import LoginRight from "@/components/login/loginRight";

import { getSiteProps } from "../../utils/siteProps";
import translation from "../../utils/translation_login.json";
import LoginFooter from "./loginFooter";
import { LoginProps } from "./types";

const cleverTapEventTypes = {
  onUserLoginPush: "onUserLoginPush",
  eventPush: "eventPush",
  profilePush: "profilePush",
  splTrackEvent: "splTrackEvent",
  getPlatform: "getPlatform",
};

const handleEventTracking = (event: EventTrackingProps) => {
  const { eventName, eventType, eventProps, action } = event;
  if (eventProps) {
    eventProps["page_name"] = "Login";
  }

  switch (eventType) {
    case cleverTapEventTypes.eventPush:
      trackClevertapEvents.eventPush(eventName, eventProps);
      break;
    case cleverTapEventTypes.onUserLoginPush:
      trackClevertapEvents.onuserLoginPush(eventProps);
      break;
    case cleverTapEventTypes.profilePush:
      trackClevertapEvents.profilePush(eventProps);
      break;
    case cleverTapEventTypes.splTrackEvent:
      trackClevertapEvents.splTrackEvent(action, eventName, eventProps);
      break;
    default:
      break;
  }
};

export const Login: React.FC<LoginProps> = (props) => {
  const { hostname, page, isMobileServer } = props;
  const isMobile = useMobile(isMobileServer);
  const siteConfig = getSiteProps(hostname);
  const [loginStep, setLoginStep] = useState("LOGIN");
  const [loginData, setLoginData] = useState({
    username: "",
    password: "",
    countryCode: "",
    reCaptchaAction: "",
    reCaptchaToken: "",
    uuid: "",
    otpId: "",
    isEmailLogin: false,
    selectedEmailFromPopup: {
      maskedEmail: "",
      uuid: "",
    },
  });
  const [toast, setToast] = useState({ show: false, type: "", message: "" });

  useEffect(() => {
    handleEventTracking({
      eventType: "eventPush",
      eventName: "Login_Page_Viewed",
      eventProps: {
        site_context: siteConfig?.site_map ?? "rexmonster",
      },
    });
  }, [hostname]);

  const showToast = useCallback((val: ToastType) => {
    setToast(val);
  }, []);

  const contextValue = useMemo(
    () => ({
      page,
      isMobile,
      hostname,
      loginStep,
      loginData,
      translation,
      showToast,
      setLoginStep,
      setLoginData,
      handleEventTracking,
    }),
    [isMobile, loginStep],
  );

  return (
    <LoginContext.Provider value={contextValue}>
      <div
        className="flex min-h-screen flex-col"
        style={{
          backgroundImage: isMobile
            ? "none"
            : "linear-gradient(to right, #f5f5f5 50%, #ffffff 50%)",
        }}
      >
        <LoginHeader />
        <div className="flex flex-1">
          <div className="flex w-full">
            {!isMobile ? (
              <div className="hidden flex-1 md:!block">
                <LoginLeft />
              </div>
            ) : null}

            <LoginRight />
          </div>
          <Toast
            position="top-center"
            showToast={toast.show}
            text={toast.message}
            type={toast.type}
            width={isMobile ? "75" : "25"}
            timer={3000}
            onClose={() => setToast((prev) => ({ ...prev, show: false }))}
          />
        </div>
        <LoginFooter />
      </div>
    </LoginContext.Provider>
  );
};
